import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { apiGet } from '../../../lib/shopperApi';
import OrderMessageThread from './OrderMessageThread';

const STATUS_FILTERS = [
  { id: 'all',        label: 'All Orders' },
  { id: 'completed',  label: 'Delivered',  match: (s) => ['completed','shipped','delivered'].includes(s) },
  { id: 'processing', label: 'Processing', match: (s) => ['processing','on-hold','pending_payment','pending'].includes(s) },
  { id: 'cancelled',  label: 'Cancelled',  match: (s) => ['cancelled','refunded','failed'].includes(s) },
];

const fmtMoney = (cents, currency) => {
  const c = (Number(cents) || 0) / 100;
  const cur = (currency || 'USD').toUpperCase();
  return `${cur} ${c.toFixed(2)}`;
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + (s.includes('T') ? '' : 'Z'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
};

const statusBadge = (status) => {
  const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
  if (['completed','shipped','delivered'].includes(status)) return `${base} bg-green-100 text-green-800`;
  if (['cancelled','refunded','failed'].includes(status))   return `${base} bg-red-100 text-red-800`;
  return `${base} bg-amber-100 text-amber-800`;
};

const statusIcon = (status) => {
  if (['completed','shipped','delivered'].includes(status)) return { icon: 'CheckCircle', color: 'text-green-500' };
  if (['cancelled','refunded','failed'].includes(status))   return { icon: 'XCircle', color: 'text-red-500' };
  return { icon: 'Clock', color: 'text-amber-500' };
};

const OrderHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null); // null = loading
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError]   = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiGet('/orders');
      if (cancelled) return;
      if (!ok) { setError(data?.error || 'Could not load orders.'); setOrders([]); return; }
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
    })();
    return () => { cancelled = true; };
  }, []);

  const filterCounts = useMemo(() => {
    const list = orders || [];
    return STATUS_FILTERS.map((f) => ({
      ...f,
      count: f.id === 'all' ? list.length : list.filter((o) => f.match && f.match(o.status)).length,
    }));
  }, [orders]);

  const filtered = useMemo(() => {
    const list = orders || [];
    return list.filter((o) => {
      const f = STATUS_FILTERS.find((x) => x.id === selectedFilter);
      const matchFilter = !f?.match ? true : f.match(o.status);
      const idStr = `#${o.id || ''}`;
      const matchSearch = !searchTerm || idStr.toLowerCase().includes(searchTerm.toLowerCase());
      return matchFilter && matchSearch;
    });
  }, [orders, selectedFilter, searchTerm]);

  return (
    <div className="max-w-6xl">
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Order History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and track all your orders</p>
            </div>
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search orders…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {filterCounts.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFilter(f.id)}
                className={cn(
                  'inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  selectedFilter === f.id ? 'bg-primary text-primary-foreground' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {f.label}
                <span className="ml-2 text-xs bg-black/10 px-1.5 py-0.5 rounded-full">{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="px-6 pt-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="divide-y divide-border">
          {orders === null ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Loading orders…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {orders.length === 0 ? 'No orders yet' : 'No orders match your search'}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {orders.length === 0 ? "You haven't placed any orders yet." : 'Try adjusting your search or filter.'}
              </p>
              <Button onClick={() => navigate('/product-catalog')}>Start Shopping</Button>
            </div>
          ) : (
            filtered.map((order) => {
              const si = statusIcon(order.status);
              const isOpen = expandedId === order.id;
              return (
                <div key={order.id} className="p-6">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : order.id)}
                    className="w-full text-left flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 group"
                    aria-expanded={isOpen}
                  >
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-medium text-foreground mb-1 flex items-center">
                            Order #{order.id}
                            <Icon
                              name="ChevronDown"
                              size={16}
                              className={cn(
                                'ml-2 text-muted-foreground transition-transform',
                                isOpen ? 'rotate-180' : 'group-hover:translate-y-0.5'
                              )}
                            />
                          </h3>
                          <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                            <span>Placed on {fmtDate(order.date_created_gmt)}</span>
                            <span>•</span>
                            <span className="flex items-center">
                              <Icon name={si.icon} size={14} className={cn('mr-1', si.color)} />
                              <span className={statusBadge(order.status)}>
                                {String(order.status || 'pending').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-foreground">{fmtMoney(order.total_amount_cents, order.currency)}</p>
                          {order.payment_method_title && (
                            <p className="text-xs text-muted-foreground mt-1">{order.payment_method_title}</p>
                          )}
                        </div>
                      </div>
                      {order.transaction_id && (
                        <p className="text-xs text-muted-foreground">Transaction: {order.transaction_id}</p>
                      )}
                      {!isOpen && (
                        <p className="text-xs text-primary mt-2 inline-flex items-center">
                          <Icon name="MessageSquare" size={12} className="mr-1" />
                          Click to view messages
                        </p>
                      )}
                    </div>
                  </button>

                  {isOpen && <OrderMessageThread orderId={order.id} />}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
